// src/views/despesasRecorrentes/DespesasRecorrentesList.jsx
import React, { useState, useEffect, useCallback } from "react";
import despesasRecorrentesService from "../../services/despesasRecorrentesService";
import fornecedoresService from "../../services/fornecedoresService";
import contasContabeisService from "../../services/contasContabeisService";
import { useAuth } from "../../contexts/AuthContext";
import unidadesService from "../../services/unidadesService";

const fmtMoeda = v => parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDt    = dt => { if (!dt) return "-"; try { return new Date((String(dt || "").split("T")[0]) + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return dt; } };

const PERIOD_LABELS = { Mensal: "Mensal", Bimestral: "Bimestral", Trimestral: "Trimestral", Semestral: "Semestral", Anual: "Anual", Quinzenal: "Quinzenal" };
const STATUS_STYLES = {
  Ativa:     { bg: "#DCFCE7", color: "#166534" },
  Pausada:   { bg: "#FEF9C3", color: "#854D0E" },
  Encerrada: { bg: "var(--gray-100)", color: "var(--gray-500)" },
};
const PC_STYLES = {
  Pendente:       { bg: "#EFF6FF", color: "var(--primary)" },
  IncluidaBoleto: { bg: "#FFF7ED", color: "#C2410C" },
  Paga:           { bg: "#DCFCE7", color: "#166534" },
  Cancelada:      { bg: "#FEE2E2", color: "#991B1B" },
};

const EMPTY_FORM = {
  nmDespesa: "", descricao: "", idFornecedor: "", idContaContabil: "",
  vlrTotal: "", nrParcelas: "1", periodicidade: "Mensal", tpBoleto: "Mensal",
  dtInicio: "", diaVencimento: "10", tpRateio: "FracaoIdeal", observacao: "",
};

// ── Banner ──────────────────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 8,
      padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

export default function DespesasRecorrentesList() {
  const { condominioAtivo, podeEditar, isAdmin } = useAuth();
  const canEdit = podeEditar("despesasRecorrentes");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [idCondo, setIdCondo] = useState("");
  const [filterStatus, setFilterStatus] = useState("Ativa");
  const [filterFornecedor, setFilterFornecedor] = useState("");
  const [filterPeriodicidade, setFilterPeriodicidade] = useState("");
  const [busca, setBusca] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editParcela, setEditParcela] = useState(null); // {id, vlrTotal, dtVencimento}
  const [savingParc,  setSavingParc]  = useState(false);
  const [editId,  setEditId]  = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [contasContabeis, setContasContabeis] = useState([]);
  const [qtdUnidades, setQtdUnidades] = useState(0);

  const [pageError,   setPageError]   = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  // Modais
  const [showAlterarStatus, setShowAlterarStatus] = useState(null); // {despesa, novoStatus}
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    if (!pageSuccess) return;
    const t = setTimeout(() => setPageSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [pageSuccess]);

  useEffect(() => {
    if (!idCondo) { setFornecedores([]); setContasContabeis([]); setQtdUnidades(0); return; }
    fornecedoresService.listar({ idCondominio: idCondo, situacao: "ativo", limit: 200 }).then(r => setFornecedores(r.data || [])).catch(() => {});
    contasContabeisService.listar({ idCondominio: idCondo, situacao: "ativo", natureza: "Analitica" }).then(r => setContasContabeis(r.data || [])).catch(() => {});
    unidadesService.listar({ idCondominio: idCondo, limit: 200 }).then(r => setQtdUnidades(r.pagination?.total || 0)).catch(() => {});
  }, [idCondo]);

  const carregar = useCallback(async () => {
    if (!idCondo) { setData([]); return; }
    setLoading(true);
    try {
      const r = await despesasRecorrentesService.listar({
        idCondominio: idCondo,
        status: filterStatus,
        idFornecedor: filterFornecedor,
        periodicidade: filterPeriodicidade,
        busca,
      });
      setData(r.data || []);
    } catch (e) {
      setPageError(e?.message || "Falha ao carregar despesas recorrentes.");
    } finally { setLoading(false); }
  }, [idCondo, filterStatus, filterFornecedor, filterPeriodicidade, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (f, v) => { setForm(p => ({ ...p, [f]: v })); setErrors(p => ({ ...p, [f]: undefined })); };

  const vlrParcela = parseFloat(form.vlrTotal || 0) / parseInt(form.nrParcelas || 1);
  const vlrPorUnidade = form.tpRateio === "Igualitario" && qtdUnidades > 0 ? (vlrParcela / qtdUnidades).toFixed(2) : null;

  const handleSave = async () => {
    const e = {};
    if (!form.nmDespesa.trim()) e.nmDespesa = "Nome obrigatório";
    if (parseFloat(form.vlrTotal || 0) <= 0) e.vlrTotal = "Valor deve ser maior que zero";
    if (!form.dtInicio) e.dtInicio = "Data de início obrigatória";
    setErrors(e); if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (editId) {
        await despesasRecorrentesService.atualizar(editId, {
          nmDespesa: form.nmDespesa, descricao: form.descricao,
          idFornecedor: form.idFornecedor || null,
          idContaContabil: form.idContaContabil || null,
          observacao: form.observacao,
          tpBoleto: form.tpBoleto,
        });
        setPageSuccess("Despesa recorrente atualizada.");
      } else {
        await despesasRecorrentesService.criar({
          ...form,
          idCondominio: idCondo,
          idFornecedor: form.idFornecedor || null,
          idContaContabil: form.idContaContabil || null,
        });
        setPageSuccess("Despesa recorrente criada.");
      }
      setShowModal(false); setForm(EMPTY_FORM); setEditId(null);
      await carregar();
    } catch (err) {
      setPageError(err?.message || "Falha ao salvar.");
    } finally { setSaving(false); }
  };

  const abrirDetalhe = async (id) => {
    if (detalhe?.id === id) { setDetalhe(null); return; }
    try {
      const r = await despesasRecorrentesService.buscarPorId(id);
      setDetalhe(r.data);
    } catch (e) { setPageError(e?.message || "Falha ao carregar detalhes."); }
  };

  const confirmarAlterarStatus = async () => {
    if (!showAlterarStatus) return;
    setActionLoading(true);
    try {
      await despesasRecorrentesService.atualizar(showAlterarStatus.despesa.id, { status: showAlterarStatus.novoStatus });
      setPageSuccess(`Despesa "${showAlterarStatus.despesa.nmDespesa}" → ${showAlterarStatus.novoStatus}.`);
      setShowAlterarStatus(null);
      await carregar();
    } catch (e) {
      setPageError(e?.message || "Falha ao alterar status.");
    } finally { setActionLoading(false); }
  };

  const handleSalvarParcela = async () => {
    if (!editParcela) return;
    setSavingParc(true);
    try {
      await despesasRecorrentesService.atualizarParcela(editParcela.id, {
        vlrTotal: editParcela.vlrTotal,
        dtVencimento: editParcela.dtVencimento,
      });
      setEditParcela(null);
      setPageSuccess("Parcela atualizada.");
      // Recarrega detalhe
      if (detalhe) {
        const r = await despesasRecorrentesService.buscarPorId(detalhe.id);
        setDetalhe(r.data);
      }
      await carregar();
    } catch (e) {
      setPageError(e?.message || "Falha ao salvar parcela.");
    } finally { setSavingParc(false); }
  };

  const exportarCSV = () => {
    if (!data.length) return;
    const header = ["Despesa","Fornecedor","Periodicidade","Tipo Boleto","Rateio","Valor Total","Início","Fim","Parcelas","Pendentes","Pagas","Status"];
    const rows = data.map(d => [
      d.nmDespesa,
      d.nmFornecedor || "",
      PERIOD_LABELS[d.periodicidade] || d.periodicidade,
      d.tpBoleto || "Mensal",
      d.tpRateio === "FracaoIdeal" ? "Fração Ideal" : d.tpRateio === "Igualitario" ? "Igualitário" : "Valor Fixo",
      parseFloat(d.vlrTotal || 0).toFixed(2),
      fmtDt(d.dtInicio),
      fmtDt(d.dtFim),
      d.nrParcelas,
      d.parcelasPendentes,
      d.parcelasPagas,
      d.status,
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `despesas-recorrentes-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const contasDespesa = contasContabeis.filter(c => c.tpConta);

  return (
    <div>
      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-arrow-repeat me-2" style={{ color: "var(--primary)", fontSize: 22 }} />Despesas Recorrentes</h1>
          <p className="page-subtitle">Despesas parceladas que alimentam os boletos automaticamente</p>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <button className="btn btn-outline-secondary btn-sm" onClick={exportarCSV} disabled={!data.length} title="Exportar CSV">
            <i className="bi bi-download me-1" /> CSV
          </button>
          <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); }}
            disabled={!idCondo || !canEdit}>
            <i className="bi bi-plus-lg" /> Nova Despesa Recorrente
          </button>
        </div>
      </div>

      {!idCondo && <Banner tipo="warning">Selecione um condomínio na barra superior para acessar este módulo.</Banner>}

      {/* Stats */}
      <div className="row g-3 mb-4">
        {[
          { icon: "bi-arrow-repeat", cls: "blue",   val: data.filter(d => d.status === "Ativa").length, label: "Ativas" },
          { icon: "bi-pause-circle", cls: "orange", val: data.filter(d => d.status === "Pausada").length, label: "Pausadas" },
          { icon: "bi-check-circle", cls: "gray",   val: data.filter(d => d.status === "Encerrada").length, label: "Encerradas" },
          { icon: "bi-cash-coin",    cls: "green",  val: fmtMoeda(data.filter(d => d.status === "Ativa").reduce((a, d) => a + parseFloat(d.vlrTotal || 0), 0)), label: "Total Ativo" },
        ].map(s => (
          <div className="col-6 col-md-3" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value" style={{ fontSize: typeof s.val === "string" ? 14 : 24 }}>{s.val}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header-custom" style={{ flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gray-700)" }}>
            Despesas Recorrentes — {condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "Selecione um condomínio"}
          </span>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <input className="form-control form-control-sm" placeholder="Buscar..."
              style={{ width: 160, fontSize: 12 }}
              value={busca} onChange={e => setBusca(e.target.value)} />
            <select className="form-select form-select-sm" style={{ width: 150, fontSize: 12 }}
              value={filterFornecedor} onChange={e => setFilterFornecedor(e.target.value)}>
              <option value="">Todos fornecedores</option>
              {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nmReduzido || f.nmRazaoSocial}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 130, fontSize: 12 }}
              value={filterPeriodicidade} onChange={e => setFilterPeriodicidade(e.target.value)}>
              <option value="">Periodicidade</option>
              {Object.entries(PERIOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {["Ativa", "Pausada", "Encerrada", ""].map(s => (
              <button key={s || "todos"} className={`btn btn-sm ${filterStatus === s ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ fontSize: 12 }} onClick={() => setFilterStatus(s)}>{s || "Todas"}</button>
            ))}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { setBusca(""); setFilterFornecedor(""); setFilterPeriodicidade(""); setFilterStatus("Ativa"); }}>
              <i className="bi bi-eraser" />
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise" /></button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-custom">
            <thead><tr>
              <th>Despesa</th><th>Fornecedor</th><th>Periodicidade</th><th>Tipo Boleto</th><th>Rateio</th>
              <th>Valor Total</th><th>Início</th><th>Fim</th><th>Parcelas</th><th>Status</th><th style={{ width: 140 }}>Ações</th>
            </tr></thead>
            <tbody>
              {loading
                ? <tr><td colSpan={11} className="text-center py-5"><span className="spinner-border spinner-border-sm text-primary me-2" /></td></tr>
                : !idCondo
                  ? <tr><td colSpan={11} className="text-center py-4" style={{ color: "var(--gray-400)" }}>Selecione um condomínio</td></tr>
                  : data.length === 0
                    ? <tr><td colSpan={11} className="text-center py-4" style={{ color: "var(--gray-400)" }}>
                        <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />Nenhuma despesa recorrente</td></tr>
                    : data.map(d => (
                      <React.Fragment key={d.id}>
                        <tr>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{d.nmDespesa}</div>
                            {d.descricao && <div style={{ fontSize: 11.5, color: "var(--gray-400)" }}>{d.descricao}</div>}
                          </td>
                          <td style={{ fontSize: 13 }}>{d.nmFornecedor || "—"}</td>
                          <td><span style={{ background:"var(--bg-highlight)", color: "var(--primary)", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{PERIOD_LABELS[d.periodicidade]}</span></td>
                          <td>
                            <span style={{
                              padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                              background: d.tpBoleto === "Avulso" ? "#FFF7ED" : d.tpBoleto === "Ambos" ? "#F0FDF4" : "#EFF6FF",
                              color:      d.tpBoleto === "Avulso" ? "#C2410C" : d.tpBoleto === "Ambos" ? "#166534" : "var(--primary)",
                            }}>
                              {d.tpBoleto === "Avulso" ? "Avulso" : d.tpBoleto === "Ambos" ? "Mensal+Avulso" : "Mensal"}
                            </span>
                          </td>
                          <td style={{ fontSize: 12.5 }}>{d.tpRateio === "FracaoIdeal" ? "Fração Ideal" : d.tpRateio === "Igualitario" ? "Igualitário" : "Valor Fixo"}</td>
                          <td style={{ fontWeight: 700 }}>{fmtMoeda(d.vlrTotal)}</td>
                          <td style={{ fontSize: 12.5 }}>{fmtDt(d.dtInicio)}</td>
                          <td style={{ fontSize: 12.5 }}>{fmtDt(d.dtFim)}</td>
                          <td>
                            <div style={{ fontSize: 12 }}>
                              <span style={{ color: "#166534", fontWeight: 600 }}>{d.parcelasPendentes} pend.</span>
                              {d.parcelasIncluidasBoleto > 0 && <span style={{ color: "#C2410C", fontWeight: 600, marginLeft: 4 }}>{d.parcelasIncluidasBoleto} bol.</span>}
                              {d.parcelasPagas > 0 && <span style={{ color: "var(--gray-400)", marginLeft: 4 }}>{d.parcelasPagas} pago</span>}
                            </div>
                            <div style={{ height: 4, background: "var(--gray-200)", borderRadius: 2, marginTop: 4 }}>
                              <div style={{ height: "100%", background: "var(--accent)", borderRadius: 2, width: `${Math.round((d.parcelasPagas / (d.nrParcelas || 1)) * 100)}%` }} />
                            </div>
                          </td>
                          <td><span style={{ ...STATUS_STYLES[d.status], padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{d.status}</span></td>
                          <td>
                            <div className="table-actions">
                              <button className="btn btn-outline-secondary btn-sm btn-icon" title="Ver parcelas" onClick={() => abrirDetalhe(d.id)}>
                                <i className="bi bi-eye" />
                              </button>
                              <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" disabled={!canEdit}
                                onClick={() => {
                                  setForm({
                                    nmDespesa: d.nmDespesa,
                                    descricao: d.descricao || "",
                                    idFornecedor: d.idFornecedor || "",
                                    idContaContabil: d.idContaContabil || "",
                                    vlrTotal: d.vlrTotal,
                                    nrParcelas: d.nrParcelas,
                                    periodicidade: d.periodicidade,
                                    tpBoleto: d.tpBoleto || "Mensal",
                                    dtInicio: d.dtInicio?.split("T")[0] || "",
                                    diaVencimento: d.diaVencimento,
                                    tpRateio: d.tpRateio,
                                    observacao: d.observacao || "",
                                  });
                                  setEditId(d.id); setShowModal(true);
                                }}>
                                <i className="bi bi-pencil" />
                              </button>
                              {d.status === "Ativa" && canEdit && (
                                <button className="btn btn-outline-warning btn-sm btn-icon" title="Pausar"
                                  style={{ borderColor: "#F59E0B", color: "#B45309" }}
                                  onClick={() => setShowAlterarStatus({ despesa: d, novoStatus: "Pausada" })}>
                                  <i className="bi bi-pause-circle" />
                                </button>
                              )}
                              {d.status === "Pausada" && canEdit && (
                                <button className="btn btn-outline-success btn-sm btn-icon" title="Reativar"
                                  onClick={() => setShowAlterarStatus({ despesa: d, novoStatus: "Ativa" })}>
                                  <i className="bi bi-play-circle" />
                                </button>
                              )}
                              {d.status === "Encerrada" && isAdmin && (
                                <button className="btn btn-outline-info btn-sm btn-icon" title="Reabrir despesa (Admin)"
                                  onClick={() => setShowAlterarStatus({ despesa: d, novoStatus: "Ativa" })}>
                                  <i className="bi bi-arrow-counterclockwise" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Detalhe parcelas inline */}
                        {detalhe?.id === d.id && (
                          <tr><td colSpan={11} style={{ padding: 0, background:"var(--bg-surface-alt)" }}>
                            <div style={{ padding: "12px 16px" }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: "var(--gray-700)", marginBottom: 10 }}>
                                <i className="bi bi-calendar3 me-2" />Parcelas de {d.nmDespesa}
                              </div>
                              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                                <table className="table table-custom" style={{ background:"var(--bg-surface)", marginBottom: 0 }}>
                                  <thead><tr>
                                    <th>#</th><th>Competência</th><th>Vencimento</th>
                                    <th style={{ textAlign: "right" }}>Valor Total</th>
                                    <th style={{ textAlign: "right" }}>Por Unidade</th>
                                    <th>Status</th><th />
                                  </tr></thead>
                                  <tbody>
                                    {(detalhe.parcelas || []).map(p => (
                                      <tr key={p.id}>
                                        <td style={{ fontWeight: 700, color: "var(--primary)" }}>{p.nrParcela}/{d.nrParcelas}</td>
                                        <td style={{ fontSize: 12.5 }}>{p.competencia}</td>
                                        <td style={{ fontSize: 12.5 }}>
                                          {editParcela?.id === p.id
                                            ? <input type="date" className="form-control form-control-sm" style={{ width: 130 }}
                                                value={editParcela.dtVencimento}
                                                onChange={e => setEditParcela(prev => ({ ...prev, dtVencimento: e.target.value }))} />
                                            : fmtDt(p.dtVencimento)}
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                                          {editParcela?.id === p.id
                                            ? <input type="number" step="0.01" className="form-control form-control-sm"
                                                style={{ width: 110, display: "inline" }}
                                                value={editParcela.vlrTotal}
                                                onChange={e => setEditParcela(prev => ({ ...prev, vlrTotal: e.target.value }))} />
                                            : fmtMoeda(p.vlrTotal)}
                                        </td>
                                        <td style={{ textAlign: "right", fontSize: 12.5, color: p.vlrPorUnidade ? "#166534" : "var(--gray-400)" }}>
                                          {p.vlrPorUnidade ? fmtMoeda(p.vlrPorUnidade) : "Calc. na geração"}
                                        </td>
                                        <td><span style={{ ...PC_STYLES[p.status], padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{p.status}</span></td>
                                        <td>
                                          {p.status === "Pendente" && canEdit && (
                                            editParcela?.id === p.id
                                              ? <div style={{ display: "flex", gap: 4 }}>
                                                  <button className="btn btn-success btn-sm" style={{ fontSize: 10, padding: "1px 6px" }}
                                                    onClick={handleSalvarParcela} disabled={savingParc}>OK</button>
                                                  <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 10, padding: "1px 6px" }}
                                                    onClick={() => setEditParcela(null)}>X</button>
                                                </div>
                                              : <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 10, padding: "1px 6px" }}
                                                  title="Editar valor/vencimento"
                                                  onClick={() => setEditParcela({
                                                    id: p.id, vlrTotal: p.vlrTotal,
                                                    dtVencimento: p.dtVencimento?.split("T")[0] || "",
                                                  })}>
                                                  <i className="bi bi-pencil" />
                                                </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal Novo/Editar ── */}
      {showModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 38, height: 38, background: "rgba(255,255,255,.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="bi bi-arrow-repeat" style={{ fontSize: 18, color: "#fff" }} />
                  </div>
                  <div>
                    <h5 className="modal-title mb-0">{editId ? "Editar" : "Nova"} Despesa Recorrente</h5>
                    <small style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>{condominioAtivo?.nmFantasia || ""}</small>
                  </div>
                </div>
                <button className="btn-close btn-close-white" onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); }} />
              </div>
              <div className="modal-body">
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-arrow-repeat" /> Identificação</div>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Nome da Despesa <span className="required-mark">*</span></label>
                      <input className={`form-control ${errors.nmDespesa ? "is-invalid" : ""}`} value={form.nmDespesa}
                        onChange={e => set("nmDespesa", e.target.value)} placeholder="Ex: Seguro Predial 2026, Manutenção Elevador..." />
                      {errors.nmDespesa && <div className="invalid-feedback">{errors.nmDespesa}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Fornecedor / Prestador</label>
                      <select className="form-select" value={form.idFornecedor} onChange={e => set("idFornecedor", e.target.value)}>
                        <option value="">— Selecione —</option>
                        {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nmReduzido || f.nmRazaoSocial}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Conta Contábil</label>
                      <select className="form-select" value={form.idContaContabil} onChange={e => set("idContaContabil", e.target.value)}>
                        <option value="">— Selecione —</option>
                        {contasDespesa.map(c => <option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Descrição</label>
                      <input className="form-control" value={form.descricao} onChange={e => set("descricao", e.target.value)} placeholder="Descrição adicional" />
                    </div>
                  </div>
                </div>

                {!editId && (
                  <div className="form-section">
                    <div className="form-section-title"><i className="bi bi-cash-coin" /> Valores e Parcelamento</div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">Valor Total <span className="required-mark">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text">R$</span>
                          <input className={`form-control ${errors.vlrTotal ? "is-invalid" : ""}`}
                            type="number" step="0.01" min="0.01" value={form.vlrTotal}
                            onChange={e => set("vlrTotal", e.target.value)} />
                          {errors.vlrTotal && <div className="invalid-feedback">{errors.vlrTotal}</div>}
                        </div>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Nº Parcelas</label>
                        <input className="form-control" type="number" min="1" max="360"
                          value={form.nrParcelas} onChange={e => set("nrParcelas", e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Periodicidade</label>
                        <select className="form-select" value={form.periodicidade} onChange={e => set("periodicidade", e.target.value)}>
                          {Object.entries(PERIOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Dia Vencimento</label>
                        <input className="form-control" type="number" min="1" max="28"
                          value={form.diaVencimento} onChange={e => set("diaVencimento", e.target.value)} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Data Início <span className="required-mark">*</span></label>
                        <input type="date" className={`form-control ${errors.dtInicio ? "is-invalid" : ""}`}
                          value={form.dtInicio} onChange={e => set("dtInicio", e.target.value)} />
                        {errors.dtInicio && <div className="invalid-feedback">{errors.dtInicio}</div>}
                      </div>

                      <div className="col-12">
                        <div style={{ background:"var(--bg-highlight)", borderRadius: 8, padding: "12px 16px", display: "flex", gap: 24, flexWrap: "wrap" }}>
                          <div><span style={{ fontSize: 12, color: "var(--gray-500)" }}>Valor por parcela: </span><strong style={{ color: "var(--primary)" }}>{fmtMoeda(vlrParcela.toFixed(2))}</strong></div>
                          {vlrPorUnidade && <div><span style={{ fontSize: 12, color: "var(--gray-500)" }}>Por unidade ({qtdUnidades} un.): </span><strong style={{ color: "#166534" }}>{fmtMoeda(vlrPorUnidade)}</strong></div>}
                          {form.tpRateio === "FracaoIdeal" && <div style={{ fontSize: 12, color: "var(--gray-400)" }}>Valor por unidade calculado proporcionalmente pela fração ideal na geração do boleto</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-diagram-3" /> Rateio entre Unidades</div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label"><i className="bi bi-receipt me-1" />Entra no Boleto</label>
                      <select className="form-select" value={form.tpBoleto || "Mensal"} onChange={e => set("tpBoleto", e.target.value)}>
                        <option value="Mensal">Mensal — apenas boleto mensal</option>
                        <option value="Avulso">Avulso — apenas cobrança avulsa</option>
                        <option value="Ambos">Ambos — mensal e avulso</option>
                      </select>
                      <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 2 }}>
                        {form.tpBoleto === "Avulso" && "Não será incluído automaticamente no boleto mensal"}
                        {form.tpBoleto === "Ambos" && "Aparece em ambos os tipos de geração"}
                        {(form.tpBoleto === "Mensal" || !form.tpBoleto) && "Incluído apenas na geração mensal automática"}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo de Rateio</label>
                      <select className="form-select" value={form.tpRateio} onChange={e => set("tpRateio", e.target.value)} disabled={!!editId}>
                        <option value="FracaoIdeal">Por Fração Ideal</option>
                        <option value="Igualitario">Igualitário ({qtdUnidades} unidades = {fmtMoeda((vlrParcela / Math.max(1, qtdUnidades)).toFixed(2))}/un.)</option>
                        <option value="ValorFixo">Valor Fixo por Unidade</option>
                      </select>
                      {editId && <small style={{ fontSize: 11, color: "var(--gray-400)" }}>Tipo de rateio não pode ser alterado após criação</small>}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Observações</label>
                      <textarea className="form-control" rows={2} value={form.observacao} onChange={e => set("observacao", e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary" onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); }}>
                  <i className="bi bi-x-circle" /> Cancelar
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving || !canEdit}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                    : <><i className="bi bi-check-circle" /> {editId ? "Salvar" : "Criar Recorrência"}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Alterar Status ── */}
      {showAlterarStatus && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${showAlterarStatus.novoStatus === "Pausada" ? "bi-pause-circle" : "bi-play-circle"} me-2`} />
                  {showAlterarStatus.novoStatus === "Pausada" ? "Pausar" : "Reativar"} Despesa Recorrente
                </h5>
                <button type="button" className="btn-close" onClick={() => !actionLoading && setShowAlterarStatus(null)} />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14 }}>
                  Confirma {showAlterarStatus.novoStatus === "Pausada" ? "pausar" : "reativar"} a despesa{" "}
                  <strong>{showAlterarStatus.despesa.nmDespesa}</strong>?
                </p>
                {showAlterarStatus.novoStatus === "Pausada" && (
                  <Banner tipo="warning">
                    Parcelas pendentes <strong>não serão incluídas</strong> nos próximos boletos enquanto estiver pausada.
                  </Banner>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowAlterarStatus(null)} disabled={actionLoading}>Voltar</button>
                <button className="btn btn-primary" onClick={confirmarAlterarStatus} disabled={actionLoading}>
                  {actionLoading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                    : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
